import type { Dispatch, SetStateAction } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import type { Asset, Book, BudgetRuleKind } from "@/schemas";

import type { BudgetFormState } from "../types";

export function RuleDialog({
  open,
  saving,
  form,
  books,
  fiatAssets,
  categoryOptions,
  editingRuleId,
  onOpenChange,
  onSubmit,
  onFormChange,
}: {
  open: boolean;
  saving: boolean;
  form: BudgetFormState;
  books: Book[];
  fiatAssets: Asset[];
  categoryOptions: string[];
  editingRuleId: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void;
  onFormChange: Dispatch<SetStateAction<BudgetFormState>>;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {editingRuleId ? "Edit budget item" : "New budget item"}
          </DialogTitle>
          <DialogDescription>
            Configure the details of this budget rule.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Type</Label>
            <Select
              value={form.kind}
              onValueChange={(value) =>
                onFormChange((current) => ({
                  ...current,
                  kind: value as BudgetRuleKind,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
                <SelectItem value="investment">Investment</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Label</Label>
            <Input
              value={form.label}
              onChange={(event) =>
                onFormChange((current) => ({
                  ...current,
                  label: event.target.value,
                }))
              }
              placeholder="e.g. Salary, Rent, Groceries"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Category</Label>
              <Input
                list={`budget-categories-${form.kind}`}
                value={form.category}
                onChange={(event) =>
                  onFormChange((current) => ({
                    ...current,
                    category: event.target.value,
                  }))
                }
                placeholder="e.g. Housing"
              />
              <datalist id={`budget-categories-${form.kind}`}>
                {categoryOptions.map((category) => (
                  <option key={category} value={category} />
                ))}
              </datalist>
            </div>
            <div className="grid gap-2">
              <Label>Amount</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={form.amount}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    onFormChange((current) => ({ ...current, amount: value }));
                  }
                }}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Currency</Label>
              <Select
                value={form.currencyAssetId}
                onValueChange={(value) =>
                  onFormChange((current) => ({
                    ...current,
                    currencyAssetId: value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {fiatAssets.map((asset) => (
                    <SelectItem key={asset.asset_id} value={asset.asset_id}>
                      {asset.symbol} - {asset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Day of month</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={form.dayOfMonth}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === "" || /^\d{0,2}$/.test(value)) {
                    onFormChange((current) => ({
                      ...current,
                      dayOfMonth: value,
                    }));
                  }
                }}
                placeholder="1"
              />
            </div>
          </div>

          {form.kind === "investment" && (
            <div className="grid gap-2">
              <Label>Linked portfolio</Label>
              <Select
                value={form.linkedBookId}
                onValueChange={(value) =>
                  onFormChange((current) => ({
                    ...current,
                    linkedBookId: value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a portfolio" />
                </SelectTrigger>
                <SelectContent>
                  {books.map((book) => (
                    <SelectItem key={book.book_id} value={book.book_id}>
                      {book.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-2">
            <Label>Notes (Optional)</Label>
            <Textarea
              value={form.notes}
              onChange={(event) =>
                onFormChange((current) => ({
                  ...current,
                  notes: event.target.value,
                }))
              }
              placeholder="Any additional details..."
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label className="text-base">Active Status</Label>
              <p className="text-sm text-muted-foreground">
                Include this item in calculations and auto-generation.
              </p>
            </div>
            <Switch
              checked={form.enabled}
              onCheckedChange={(checked) =>
                onFormChange((current) => ({ ...current, enabled: checked }))
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={saving}>
            {editingRuleId ? "Save changes" : "Create item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
